// utils/resizeImage/resizeImageToBase64.ts (React Web)

import type { ResizeImageToBase64Function } from "./resizeImageToBase64.types";
import {
    canonicalizeDataUrl,
    isSupportedForResize,
} from "./resizeImageToBase64.utils";

export type { ResizeImageToBase64Function } from "./resizeImageToBase64.types";

// Formats décodables par <img> + canvas dans tous les navigateurs supportés.
// HEIC volontairement exclu : non décodable hors Safari récent.
const SUPPORTED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/**
 * Resize image to specified size (Web version)
 */
export const resizeImageToBase64: ResizeImageToBase64Function = async (
    dataImage: string,
    maxSize: number = 50,
): Promise<string> => {
    const normalizedInput = canonicalizeDataUrl(dataImage);

    if (!isSupportedForResize(normalizedInput, SUPPORTED_MIME_TYPES)) {
        return dataImage;
    }

    return new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Nécessaire pour les images cross-origin

        img.onload = () => {
            // Calcul des dimensions en préservant le ratio
            const { width, height } = calculateDimensions(
                img.width,
                img.height,
                maxSize,
            );

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
                reject("Canvas context not available");
                return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // Compression 0.8 pour qualité/taille optimale
            const resizedBase64 = canvas.toDataURL("image/png");
            resolve(resizedBase64);
        };

        img.onerror = () => reject("Failed to load image");
        img.src = normalizedInput;
    });
};

/**
 * Calcule les dimensions en préservant le ratio d'aspect
 */
function calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxSize: number,
): { width: number; height: number } {
    if (originalWidth <= maxSize && originalHeight <= maxSize) {
        return { width: originalWidth, height: originalHeight };
    }

    const ratio = Math.min(maxSize / originalWidth, maxSize / originalHeight);
    return {
        width: Math.round(originalWidth * ratio),
        height: Math.round(originalHeight * ratio),
    };
}
